'use client';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useQuery } from 'react-query';
import { useFetch } from './FetchProvider';
import {
  addToPlaylist,
  copyToAnotherPlaylist,
  createPlaylist,
  deleteUserPlaylist,
  fetchUserPlaylists,
  fetchUserPlaylistTitles,
  removeFromPlaylist,
  renamePlaylist,
  followPlaylist,
  reorderPlaylistItems,
} from '../lib/frontend/playlist';
import {
  PlaylistCreateRes,
  PlaylistGroupProps,
  PlaylistLearnNodeItem,
  PlaylistsRes,
  PlaylistStateType,
  PlaylistType,
  PlaylistProps,
} from '@osmosis/shared/types/playlist';

import { useUserContext } from './UserContext';
import { track } from '../lib/util/tracking';
import {
  getAddToPlaylistMessage,
  getRemoveFromPlaylistMessage,
  playlistMessages,
} from '../constants/playlist';
import { useIntl } from 'react-intl';
import { formatGroups } from '../lib/shared/playlist/utils';
import type { CustomSnackbarProps } from '@osmosis/nextjs-components/Snackbar';

type onAddSnackBar = (args: CustomSnackbarProps) => void;

export type AddPlaylistVideosProps = {
  items: PlaylistLearnNodeItem[];
  playlistName: string;
  playlistUrl: string;
  options?: { source?: string; trackingVideoType?: string };
  onAddSnackBar?: onAddSnackBar;
};

export type CreatePlaylistProps = {
  title: string;
  options?: { source?: string };
  onAddSnackBar?: onAddSnackBar;
};

export type CopyPlaylistVideosProps = {
  items: PlaylistLearnNodeItem[];
  anotherPlaylistName: string;
  anotherPlaylistUrl: string;
  type: PlaylistType;
  onAddSnackBar?: onAddSnackBar;
};

export type RemovePlaylistVideosProps = {
  items: PlaylistLearnNodeItem[];
  playlistName: string;
  playlistUrl: string;
  onAddSnackBar?: onAddSnackBar;
};

export type PlaylistContextProps = {
  playlists: PlaylistProps[];
  savedPlaylist?: PlaylistProps | undefined;
  updatePlaylists: () => Promise<void>;
  warmPlaylistCache: () => Promise<void>;
  reorderItems: (nodeIDs: number[], url: string) => Promise<void>;
  deletePlaylist: (url: string) => Promise<PlaylistsRes>;
  copyPlaylistVideos: ({
    items,
    anotherPlaylistName,
    anotherPlaylistUrl,
    type,
    onAddSnackBar,
  }: CopyPlaylistVideosProps) => Promise<PlaylistsRes>;
  addPlaylistVideos: ({
    items,
    playlistName,
    playlistUrl,
    options,
    onAddSnackBar,
  }: AddPlaylistVideosProps) => Promise<{
    success: PlaylistStateType;
    playlist: PlaylistProps;
  }>;
  removePlaylistVideos: ({
    items,
    playlistName,
    playlistUrl,
    onAddSnackBar,
  }: RemovePlaylistVideosProps) => Promise<PlaylistsRes>;
  createPlaylist: ({
    title,
    options,
    onAddSnackBar,
  }: CreatePlaylistProps) => Promise<PlaylistCreateRes>;
  renamePlaylist: (
    newTitle: string,
    playlistUrl: string,
  ) => Promise<PlaylistsRes>;
  followPlaylist: (
    playlistUrl: string,
    saveType: PlaylistStateType,
  ) => Promise<PlaylistsRes>;
  groups: PlaylistGroupProps[];
  isLoading: boolean;
};

const initialPlaylistResult = {
  playlists: [],
  savedPlaylists: { follow: [] },
  success: 1,
};

const defaultPlaylist: PlaylistProps = {
  followers: [],
  list: [],
  quizAllData: [],
  created: '',
  modified: '',
  playlistID: 0,
  progress: 0,
  title: '',
  type: PlaylistType.WATCH_LATER,
  url: '',
  userID: null,
  user: {
    preferredName: '',
    lastName: '',
  },
  userOwned: false,
  media: {
    ques: {},
    fc: [],
  },
};

const initialPlaylistAddVideoResponse = {
  playlist: defaultPlaylist,
  success: 1 as PlaylistStateType,
};

const initialPlaylistCreateResult = {
  url: '',
  playlistID: -1,
  title: '',
  userID: -1,
  list: [],
  success: 1,
};

const PlaylistContext = createContext<PlaylistContextProps>({
  playlists: [],
  groups: [],
  reorderItems: () => Promise.resolve(),
  deletePlaylist: () => Promise.resolve(initialPlaylistResult),
  createPlaylist: () => Promise.resolve(initialPlaylistCreateResult),
  renamePlaylist: () => Promise.resolve(initialPlaylistResult),
  addPlaylistVideos: () => Promise.resolve(initialPlaylistAddVideoResponse),
  copyPlaylistVideos: () => Promise.resolve(initialPlaylistResult),
  removePlaylistVideos: () => Promise.resolve(initialPlaylistResult),
  updatePlaylists: () => Promise.resolve(),
  warmPlaylistCache: () => Promise.resolve(),
  followPlaylist: () => Promise.resolve(initialPlaylistResult),
  isLoading: false,
} as unknown as PlaylistContextProps);

type Props = {
  children: JSX.Element;
};

export const PLAYLIST_TRACK_EVENT = 'playlist_interaction';

export const PLAYLIST_TRACK_ACTION_MAP = {
  remove: 'remove-video',
  add: 'add-video',
  create: 'create',
  follow: 'follow',
  unFollow: 'un-follow',
};

/**
 * PlaylistProvider.
 */
const PlaylistProvider = (props: PropsWithChildren<Props>) => {
  const { children } = props;

  const { get, post } = useFetch();
  const { user } = useUserContext();
  const intl = useIntl();

  // cache with the full playlist data
  const { data: _fullGroups = [], refetch: refetchFull } = useQuery(
    ['user-playlists', user],
    async () => {
      if (user) {
        return fetchUserPlaylists(get);
      }

      return [];
    },
    { enabled: false },
  );

  // get titles, type only, so the side nav renders fast.
  const {
    data: _titleGroups = [],
    refetch: refetchTitles,
    isLoading,
  } = useQuery(['user-playlist-titles', user], async () => {
    if (user) {
      return fetchUserPlaylistTitles(get);
    }

    return [];
  });

  const hasWarmedCache = useRef(false);

  const warmPlaylistCache = async () => {
    if (hasWarmedCache.current) {
      return;
    }
    hasWarmedCache.current = true;
    await refetchFull();
  };
  // Use the full playlist data once it's available; otherwise fall back to the
  // lightweight titles response so the UI can render immediately.
  const _groups = _fullGroups.length ? _fullGroups : _titleGroups;

  const groups = useMemo(
    () => formatGroups([..._groups], user),
    [_groups, user],
  );

  const playlists = groups
    .filter(
      (gr) =>
        gr.type === PlaylistType.USER_OWNED ||
        gr.type === PlaylistType.WATCH_LATER,
    )
    .map((l) => l.playlists)
    .flat(1);

  const updatePlaylists = async () => {
    await Promise.all([refetchTitles(), refetchFull()]);
  };

  const deletePlaylist = async (url: string) => {
    const res = await deleteUserPlaylist(post, url);
    if (res?.success) {
      await updatePlaylists();
    }

    return res;
  };

  const addPlaylistVideos = async ({
    items,
    playlistName,
    playlistUrl,
    options,
    onAddSnackBar,
  }: AddPlaylistVideosProps): Promise<{
    success: PlaylistStateType;
    playlist: PlaylistProps;
  }> => {
    const nodeIDs = items.map((i) => i.learnNodeID);
    const addQuizData = Boolean(items[0]?.addQuizData);

    const res = await addToPlaylist(post, {
      url: playlistUrl,
      nodeIDs,
      addQuizData,
    });

    if (res?.success) {
      if (onAddSnackBar) {
        onAddSnackBar({
          message: getAddToPlaylistMessage({
            intl,
            items,
            playlistName,
          }),
          variant: 'positive',
        });
      }

      track(
        PLAYLIST_TRACK_EVENT,
        {
          action: PLAYLIST_TRACK_ACTION_MAP.add,
          playlistID: playlistUrl,
          is_owner: true,
          is_follower: false,
          source: options?.source,
          content_type: options?.trackingVideoType,
          nodeIDs: nodeIDs,
        },
        ['keen', 'pendo'],
      );

      return res;
    }

    return initialPlaylistAddVideoResponse;
  };

  const copyPlaylistVideos = async ({
    items,
    anotherPlaylistName,
    anotherPlaylistUrl,
    type,
    onAddSnackBar,
  }: CopyPlaylistVideosProps) => {
    const args = {
      nodeIDs: items.map((i) => i.learnNodeID),
      url: anotherPlaylistUrl,
      type,
    };

    const res = await copyToAnotherPlaylist(post, args);

    if (res?.success) {
      if (onAddSnackBar) {
        onAddSnackBar({
          message: getAddToPlaylistMessage({
            intl,
            items,
            playlistName: anotherPlaylistName,
          }),
          variant: 'positive',
        });
      }

      await updatePlaylists();
    }

    return res;
  };

  const removePlaylistVideos = async ({
    items,
    playlistName,
    playlistUrl,
    onAddSnackBar,
  }: RemovePlaylistVideosProps) => {
    const args = {
      nodeIDs: items.map((i) => i.learnNodeID),
      url: playlistUrl,
    };

    const res = await removeFromPlaylist(post, args);
    if (res?.success) {
      if (onAddSnackBar) {
        onAddSnackBar({
          message: getRemoveFromPlaylistMessage({
            intl,
            items,
            playlistName,
          }),
          variant: 'negative',
        });
      }

      track(
        PLAYLIST_TRACK_EVENT,
        {
          action: PLAYLIST_TRACK_ACTION_MAP.remove,
          playlistID: args.url,
          is_follower: false,
          is_owner: true,
          nodeIDs: args.nodeIDs,
        },
        ['keen', 'pendo'],
      );
    }

    return res;
  };

  const reorderItems = async (nodeIDs: number[], playlistUrl: string) => {
    await reorderPlaylistItems(post, { nodeIDs, url: playlistUrl });
    await updatePlaylists();
  };

  const createNewPlaylist = async ({
    title,
    options,
    onAddSnackBar,
  }: CreatePlaylistProps) => {
    const res = await createPlaylist(post, title);

    if (res?.alreadyExists && onAddSnackBar) {
      onAddSnackBar({
        message: playlistMessages.alreadyExists,
        variant: 'negative',
      });
    }

    if (res?.success) {
      await updatePlaylists();
      track(
        PLAYLIST_TRACK_EVENT,
        {
          action: PLAYLIST_TRACK_ACTION_MAP.create,
          source: options?.source || 'unknown-source',
          playlistID: res.url,
          is_owner: true,
          is_follower: false,
        },
        ['keen', 'pendo'],
      );
    }

    return res;
  };

  const playlistRename = async (newTitle: string, playlistUrl: string) => {
    const res = await renamePlaylist(post, newTitle, playlistUrl);

    return res;
  };

  const playlistFollow = async (
    playlistUrl: string,
    saveType: PlaylistStateType,
  ) => {
    const res = await followPlaylist(post, playlistUrl, saveType);
    if (res?.success) {
      await updatePlaylists();
      track(
        PLAYLIST_TRACK_EVENT,
        {
          action: saveType ? 'follow' : 'un-follow',
          is_owner: false,
          is_follower: !!saveType,
          playlistID: playlistUrl,
        },
        ['keen', 'pendo'],
      );
    }

    return res;
  };

  // "Watch later" playlist
  const savedPlaylist = groups.find((p) => p.type === PlaylistType.WATCH_LATER)
    ?.playlists[0];

  return (
    <PlaylistContext.Provider
      value={{
        groups,
        playlists,
        savedPlaylist,
        warmPlaylistCache,
        reorderItems,
        updatePlaylists,
        deletePlaylist,
        copyPlaylistVideos,
        removePlaylistVideos,
        addPlaylistVideos,
        createPlaylist: createNewPlaylist,
        renamePlaylist: playlistRename,
        followPlaylist: playlistFollow,
        isLoading,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};

/**
 * UsePlaylistContext.
 */
export const usePlaylistContext = () => useContext(PlaylistContext);

export default PlaylistProvider;
